import { ComponentProps, ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { useToggleV2, useWatch } from 'hooks';
import { cn } from 'styles';
import { safeStateUpdate } from 'utils/react';

import { OverlayCommonProps } from './types';
import { Column } from '../Containers';
import { OverlayHeader } from './Header';
import { XIcon } from '../Icons';
import { OVERLAY_BG_IDS } from './escape';

const DrawerRoot = ({
  shouldScaleBackground = true,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Root>): JSX.Element => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    setBackgroundColorOnScale={false}
    {...props}
  />
);

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerBackground = forwardRef<
  ElementRef<typeof DrawerPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, id, ...props }: Pick<OverlayCommonProps, 'className' | 'id'>, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-black/80 dark:bg-black/60', className)}
    id={id ?? OVERLAY_BG_IDS.drawer}
    {...props}
  />
));

DrawerBackground.displayName = 'DrawerBackground';

type ContentProps = Pick<
  OverlayCommonProps,
  | 'className'
  | 'children'
  | 'gap'
  | 'title'
  | 'description'
  | 'showCloseButton'
  | 'close'
  | 'preventOpenAutoFocus'
  | 'id'
> & {
  enforceDrawerMinHeight?: boolean;
};

function DrawerContent({
  children,
  className,
  title,
  description,
  gap,
  showCloseButton,
  close,
  enforceDrawerMinHeight = false,
  preventOpenAutoFocus,
  id,
  ...props
}: ContentProps) {
  return (
    <DrawerPortal>
      <DrawerBackground id={id} />

      {/* <div className="fixed inset-0 z-50 flex items-end"> */}
      <DrawerPrimitive.Content
        {...props}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onOpenAutoFocus={preventOpenAutoFocus ? (e) => e.preventDefault() : undefined}
        // warning: max-h more than that can cause issues on ios phones with headers

        /*
          warning: will-change needs to be set to auto, otherwise any react-select inside the drawer
          can't calculate the positioning properly due to the drawer content becoming a containing block

          This is also due to the fact that react-select in the app uses fixed positioning + can't use the
          body as portalTarget as the 'modal' behavior of the drawer (and other raddix overlays) fiddles with
          that

          The comment div above <DrawerPrimitive.Content /> is here to illustrate you can remove the "fixed"
          position of the content (alongside the bottom-inset) and still have the same drawer-like behavior
          This can be an option if other fixed elements get stuck to the content (similar to what we did on <Modal />)
        */
        className="fixed inset-x-0 bottom-0 z-50 mt-24 h-auto max-h-[82vh] rounded-t-[10px] border bg-background !will-change-auto"
        // className="z-50 mt-24 h-auto max-h-[82vh] w-full rounded-t-[10px] border bg-background !will-change-auto"
      >
        <div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />

        <Column
          gap={gap}
          className={cn(
            'max-h-[82vh] px-5 pb-4 pt-2 phone:px-3',
            enforceDrawerMinHeight && 'min-h-[35vh]',
            className,
          )}
        >
          <OverlayHeader title={title} description={description} />

          {children}
        </Column>

        {showCloseButton && (
          <DrawerPrimitive.Close
            onClick={close}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <XIcon size={16} />
            <span className="sr-only">Close</span>
          </DrawerPrimitive.Close>
        )}
      </DrawerPrimitive.Content>
      {/* </div> */}
    </DrawerPortal>
  );
}

type DrawerProps = OverlayCommonProps & Pick<ContentProps, 'enforceDrawerMinHeight'>;

export function Drawer({
  isOpen: controlledIsOpen,
  close,
  children,
  allowEscape = true,
  trigger,
  ...props
}: DrawerProps): JSX.Element {
  /*
    Drawer was calling "close" twice sometimes on swipes to close

    This means it could flip togglers or other non ideal cases
  */

  const [isOpened, { setIsOn, turnOff, turnOn }] = useToggleV2(controlledIsOpen);

  useWatch(controlledIsOpen, () => (controlledIsOpen ? turnOn() : turnOff()));

  const actualClose = () => {
    setIsOn(false);

    if (close) safeStateUpdate(close);
  };

  // We have trouble determining the swipe down occur and not the click-outside
  const canEscape = allowEscape || !!trigger;

  const handleOpenChange = (newState: boolean): void => {
    if (newState) return setIsOn(newState);

    if (!canEscape) return;

    actualClose();
  };

  return (
    <DrawerRoot
      open={isOpened}
      onOpenChange={handleOpenChange}
      dismissible={canEscape}
      repositionInputs={false}
    >
      <DrawerContent {...props} close={actualClose}>
        {children}
      </DrawerContent>

      {trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>}
    </DrawerRoot>
  );
}
